package ru.villawonka.app.presentation.screens.settings.compose

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.core.screen.ScreenKey
import cafe.adriel.voyager.core.screen.uniqueScreenKey
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.bottomSheet.LocalBottomSheetNavigator
import ru.villawonka.app.getTelegram
import ru.villawonka.app.presentation.model.toCountryUIModel
import ru.villawonka.app.presentation.model.toCurrencyUIModel
import ru.villawonka.app.presentation.model.toLanguageUIModel
import ru.villawonka.app.presentation.model.toVariantPickerItemUIModel
import ru.villawonka.app.presentation.screens.settings.compose.layout.SettingsRootLayout
import ru.villawonka.app.presentation.screens.settings.viewmodel.SettingsUIEvent
import ru.villawonka.app.presentation.screens.settings.viewmodel.SettingsUIState
import ru.villawonka.app.presentation.screens.settings.viewmodel.SettingsViewModel
import ru.villawonka.app.presentation.screens.variantPicker.compose.VariantPickerScreen
import ru.villawonka.app.AppConfig
import ru.villawonka.app.resources.AppResources

class SettingsScreen : Screen {

    override val key: ScreenKey = uniqueScreenKey

    @Composable
    override fun Content() {
        getTelegram()?.setBackButtonVisible(true)

        val viewModel = koinScreenModel<SettingsViewModel>()
        val state by viewModel.uiState.collectAsStateWithLifecycle(SettingsUIState.Loading)

        SettingsRootLayout(
            state = state,
            callbacks = remember {
                SettingsCallbacks(
                    onLanguageItemClick = viewModel::onLanguageMenuItemClick,
                    onCountryItemClick = viewModel::onCountryMenuItemClick,
                    onCurrencyItemClick = viewModel::onCurrencyMenuItemClick,
                    onServiceNewsItemClick = viewModel::onServiceNewsItemClick,
                    onContactSupportItemClick = viewModel::onContactSupportItemClick,
                    onPrivacyPolicyItemClick = viewModel::onPrivacyPolicyItemClick,
                    onTermsOfUseItemClick = viewModel::onTermsOfUseItemClick,
                    onRetryButtonClick = viewModel::onRetryButtonClick
                )
            }
        )

        registerEventListener(viewModel)
    }

    @Composable
    private fun registerEventListener(viewModel: SettingsViewModel) {
        val bottomSheetNavigator = LocalBottomSheetNavigator.current
        LaunchedEffect(Unit) {
            viewModel.events.collect { event ->
                when (event) {
                    is SettingsUIEvent.OpenLanguagePicker -> {
                        bottomSheetNavigator.show(
                            VariantPickerScreen(
                                input = VariantPickerScreen.Params.Input(
                                    title = AppResources.strings.languagePickerTitle,
                                    selectedItem = event.selectedItem?.toVariantPickerItemUIModel(),
                                    items = event.items.map { it.toVariantPickerItemUIModel() },
                                )
                            ) { outputParams ->
                                outputParams.selectedItem.toLanguageUIModel()?.let {
                                    viewModel.onLanguageWasChanged(it)
                                }
                            }
                        )
                    }
                    is SettingsUIEvent.OpenCountryPicker -> {
                        bottomSheetNavigator.show(
                            VariantPickerScreen(
                                input = VariantPickerScreen.Params.Input(
                                    title = AppResources.strings.countryPickerTitle,
                                    selectedItem = event.selectedItem?.toVariantPickerItemUIModel(),
                                    items = event.items.map { it.toVariantPickerItemUIModel() },
                                )
                            ) { outputParams ->
                                outputParams.selectedItem.toCountryUIModel()?.let {
                                    viewModel.onCountryWasChanged(it)
                                }
                            }
                        )
                    }
                    is SettingsUIEvent.OpenCurrencyPicker -> {
                        bottomSheetNavigator.show(
                            VariantPickerScreen(
                                input = VariantPickerScreen.Params.Input(
                                    title = AppResources.strings.currencyPickerTitle,
                                    selectedItem = event.selectedItem?.toVariantPickerItemUIModel(),
                                    items = event.items.map { it.toVariantPickerItemUIModel() },
                                )
                            ) { outputParams ->
                                outputParams.selectedItem.toCurrencyUIModel()?.let {
                                    viewModel.onCurrencyWasChanged(it)
                                }
                            }
                        )
                    }
                    is SettingsUIEvent.OpenServiceNews -> {
                        getTelegram()?.openTelegramLink(AppConfig.TELEGRAM_CHANNEL_LINK)
                    }
                    is SettingsUIEvent.OpenContactSupport -> {
                        getTelegram()?.openTelegramLink(AppConfig.SUPPORT_LINK)
                    }
                    is SettingsUIEvent.OpenPrivacyPolicy -> {
                        getTelegram()?.openLink(AppConfig.PRIVACY_POLICY_LINK)
                    }
                    is SettingsUIEvent.OpenTermsOfUse -> {
                        getTelegram()?.openLink(AppConfig.TERMS_OF_USE_LINK)
                    }
                }
            }
        }
    }
}

data class SettingsCallbacks(
    val onLanguageItemClick: () -> Unit,
    val onCountryItemClick: () -> Unit,
    val onCurrencyItemClick: () -> Unit,
    val onServiceNewsItemClick: () -> Unit,
    val onContactSupportItemClick: () -> Unit,
    val onPrivacyPolicyItemClick: () -> Unit,
    val onTermsOfUseItemClick: () -> Unit,
    val onRetryButtonClick: () -> Unit,
)